import { ElementController, getData } from 'js-common';
import { tns } from 'tiny-slider/src/tiny-slider';
import debounce from 'lodash/debounce';

// Class name set on a slider that is currently being 'used'
export const SLIDER_INTERACTING_CLASS = 'slider--interacting';

const SLIDE_GUTTER = 30;
const SLIDER_PRE_INIT_CLASS = 'slider-pre-init';
const SLIDER_POST_INIT_CLASS = 'slider-post-init';
const SLIDER_AT_START_CLASS = 'slider-at-start';
const SLIDER_AT_END_CLASS = 'slider-at-end';

/**
 * Controller for article sliders using tiny-slider.
 *
 * @extends {ElementController}
 */
export default class ArticleSlider extends ElementController {
  static id = 'ArticleSlider';

  /**
   * DOM loaded, initialize slider.
   */
  init() {
    const sliderNode = this.getSingleNode('slider');
    if (!sliderNode) {
      throw new Error('Missing container for slider init.');
    }

    this.on(window, 'resize', this.afterResize);

    // Do the pre/post init classes before initializing the slider (instead of
    // after in the `onInit` callback) to ensure any styling changes don't
    // interfere with dimension calculations.
    this.getRoot().classList.remove(SLIDER_PRE_INIT_CLASS);
    this.getRoot().classList.add(SLIDER_POST_INIT_CLASS);

    // NOTE! The responsive option works like min-width, so all default options
    // will be applied from the smallest screen and responsive will override
    // when getting larger.
    const slideWidth = getData(this.getRoot(), 'slide-width');
    this.slider = tns({
      container: sliderNode,
      controlsContainer: this.getSingleNode('slider-controls'),
      // Prev/next button state doesn't work properly with autoWidth
      // autoWidth: true,
      fixedWidth: slideWidth * 0.9,
      gutter: SLIDE_GUTTER / 2,
      center: true,
      loop: false,
      mouseDrag: true,
      nav: false,
      onInit: this.onSliderInit,
      slideBy: 1,
      responsive: {
        375: {
          fixedWidth: slideWidth,
        },
        400: {
          gutter: SLIDE_GUTTER,
          center: false,
        },
        750: {
          slideBy: 2,
        },
      },
    });

    this.slider.events.on('indexChanged', this.handleSliderIndexChange);

    // Toggle class when interacting, primarily for the modal module to use.
    // Interactions with clickable elements isn't prevented when dragging, so
    // dragging on a card in the slider will open a modal when releasing.
    // The modal module can check for the class to prevent this.
    this.slider.events.on('dragMove', this.handleInteractStart);
    this.slider.events.on('dragEnd', this.handleInteractEnd);
  }

  /**
   * Re-check position classes after window resize.
   */
  afterResize = debounce(() => {
    this.setPositionClasses(this.slider.getInfo());
  }, 200);

  /**
   * Set classes on the slider container indicating the current position.
   *
   * @param {Object} sliderInfo - Slider data.
   */
  setPositionClasses(sliderInfo) {
    const rootClassList = this.getRoot().classList;

    // The controls are hidden if all the items fit
    if (
      sliderInfo.controlsContainer &&
      sliderInfo.controlsContainer.style.display === 'none'
    ) {
      rootClassList.add(SLIDER_AT_START_CLASS);
      rootClassList.add(SLIDER_AT_END_CLASS);
    } else {
      // Is at the beginning
      if (sliderInfo.prevButton.disabled) {
        rootClassList.add(SLIDER_AT_START_CLASS);
      } else {
        rootClassList.remove(SLIDER_AT_START_CLASS);
      }

      // Is at the end
      if (sliderInfo.nextButton.disabled) {
        rootClassList.add(SLIDER_AT_END_CLASS);
      } else {
        rootClassList.remove(SLIDER_AT_END_CLASS);
      }
    }
  }

  /**
   * Callback for when the slider has been initialized.
   *
   * @param {Object} sliderInfo - Slider data.
   */
  onSliderInit = (sliderInfo) => {
    this.setPositionClasses(sliderInfo);

    // The controls label is hard coded into the library and can't be set
    // with an option. Using the prevButton and nextButton options instead
    // prevents this, but instead results in tabindex -1 for the buttons,
    // making them inaccessible.
    sliderInfo.controlsContainer.setAttribute(
      'aria-label',
      getData(this.getRoot(), 'controls-label'),
    );
  };

  /**
   * Callback for when the current slide index has changed.
   *
   * Runs when pressing a control arrow or when a mouse/finger drag has been
   * released.
   *
   * @param {Object} sliderInfo - Slider data.
   */
  handleSliderIndexChange = (sliderInfo) => {
    this.setPositionClasses(sliderInfo);
  };

  /**
   * Set an interaction class name when starting to interact with the slider.
   */
  handleInteractStart = debounce(
    () => {
      this.getRoot().classList.add(SLIDER_INTERACTING_CLASS);
    },
    50,
    { leading: true, trailing: false },
  );

  /**
   * Remove the interaction class name when 'leaving' the slider.
   */
  handleInteractEnd = debounce(() => {
    this.getRoot().classList.remove(SLIDER_INTERACTING_CLASS);
  }, 200);
}
